import {
  getSubmissionById,
  getCommentsForSubmission,
  getOverComments,
} from '../service';

const PREFETCH_CONCURRENCY = 3;

// Use the same keys as FeedbacksRoot, including the explicit latest-version selector.
// Limit concurrency so opening a large class does not flood the API.
// Cancellation only stops this queue; reads already shared with the viewer finish.
export const prefetchSubmissionFeedback = async (
  queryClient,
  submissionIds,
  { force = false, signal } = {}
) => {
  const pending = [...new Set((submissionIds || []).filter(Boolean))];
  const options = { suppressToast: true, suppressRedirect: true };
  const fetchQuery = async (queryKey, queryFn) => {
    const queryOptions = {
      queryKey,
      queryFn: async () => {
        const before =
          queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
        const result = await queryFn();
        const current = queryClient.getQueryState(queryKey);
        // The teacher may open and edit a warmed response before this GET
        // returns. An intervening cache write belongs to that newer interaction.
        return current?.dataUpdateCount > before && current.data !== undefined
          ? current.data
          : result;
      },
      staleTime: force ? 0 : 60_000,
      cacheTime: 10 * 60_000,
    };
    // A forced prefetch would otherwise join a read started before marking
    // finished. Let that shared read settle, then fetch the completed run.
    if (
      force &&
      queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching'
    ) {
      await queryClient.prefetchQuery(queryOptions);
    }
    if (!signal?.aborted) await queryClient.prefetchQuery(queryOptions);
  };
  const fetchOne = async (id) => {
    const params = { submissionId: id, version: null };
    await Promise.allSettled(
      [
        [['submissionById', id], () => getSubmissionById(id, options)],
        [
          ['commentsById', params],
          () => getCommentsForSubmission(params, options),
        ],
        [
          ['overAllCommentsById', params],
          () => getOverComments(params, options),
        ],
      ].map(([queryKey, queryFn]) => fetchQuery(queryKey, queryFn))
    );
  };
  await Promise.all(
    Array.from(
      { length: Math.min(PREFETCH_CONCURRENCY, pending.length) },
      async () => {
        while (pending.length && !signal?.aborted)
          await fetchOne(pending.shift());
      }
    )
  );
};
